import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import App from "./App";
import './index.css';

// Initialize i18n
import "./i18n";

// MSAL imports
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from "@azure/msal-browser";
import { createMsalConfig } from "./config/authConfig";
import { AuthProvider } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import { useAuthPersistence } from "./hooks/session";
import { setMsalInstance } from "./config/msalInstance";

// Variable global para la instancia MSAL
export let msalInstance: PublicClientApplication;

/**
 * Componente que maneja la inicialización y persistencia de la sesión
 */
const AppWithAuth: React.FC = () => {
  const { isLoading } = useAuthPersistence(msalInstance);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <div>Loading...</div>
        <div style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
          Restoring session...
        </div>
      </div>
    );
  }

  return (
    <Router>
      <I18nProvider>
        <AuthProvider
          msalInstance={msalInstance}
          apiBaseUrl={import.meta.env.VITE_API_BASE_URL || ""}
          defaultScopes={["User.Read"]}
        >
          <App pca={msalInstance} />
        </AuthProvider>
      </I18nProvider>
    </Router>
  );
};

/**
 * Inicializa MSAL con configuración cargada desde JSON
 */
async function initializeApp() {
  try {
    // Cargar configuración desde JSON
    const msalConfig = await createMsalConfig();
    console.log("MSAL configuration loaded from JSON");

    // Crear instancia MSAL con configuración cargada
    msalInstance = new PublicClientApplication(msalConfig);

    // Establecer la instancia en el módulo separado
    setMsalInstance(msalInstance);

    // Inicializar MSAL
    await msalInstance.initialize();

    // Add event callback to handle login success
    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
    });

    // Render the app with secure auth provider
    createRoot(document.getElementById("root")!).render(
      <StrictMode>
        <AppWithAuth />
      </StrictMode>
    );
  } catch (error) {
    console.error("Application initialization failed:", error);
  }
}

// Inicializar la aplicación
initializeApp();
